/**
 * @file: useConversationCoordinator.ts
 *
 * The conversation's UI-coordination layer AND its event bus. It owns the
 * intent subjects through which the parts of a conversation talk WITHOUT
 * referencing each other: a bubble expresses an intent (tap a quote, play
 * audio, retry, …), the coordinator emits it on the bus, and whichever hook
 * owns that concern subscribes and handles it. Nobody calls anybody.
 *
 *   bubble  →  onQuotePress   →  scrollToMessageRequested$  →  store pages
 *   store   →  scrollToLoadedMessage$                       →  scroll jumps
 *   bubble  →  onAudioPlay    →  audioPlayRequested$        →  store marks
 *   bubble  →  onRetry        →  retryRequested$            →  pipeline retries
 *   store   →  messageInserted$ / messagesResynced$         →  scroll follows
 *   pipeline → willSendMsg$                                 →  store inserts temp
 *   pipeline → messageSent$    → store reconciles + this hook materialises handle
 *   pipeline → beforeResend$                                →  store drops temp
 *   composer → typing$ / endTyping$            →  this hook broadcasts the frame
 *
 * Domain hooks receive the coordinator and subscribe; the coordinator never
 * imports them, so the graph is acyclic. A few cross-cutting concerns it
 * subscribes to its OWN bus to handle (handle materialisation off
 * `messageSent$`, typing broadcast off `typing$`/`endTyping$`) — these need
 * the provider's `conversationHandle` + the session, which it takes / self-serves.
 * View-local concerns (image preview, read-by, card nav) it owns outright.
 *
 * The intents are plain Subjects (no replay), which is safe because every
 * emit is either a user gesture or a post-mount store mutation — never a
 * synchronous emit during render — so no intent fires before its handler's
 * `useEffect` subscription is in place.
 */

import type { AppNavParams } from '@euler/app/Routes';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import {
  ChatMessagePayload,
  ChatMsg,
  ChatUserMessagePayload,
} from '@euler/model/chat';
import { onErrorIgnore } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo } from 'react';
import { Linking } from 'react-native';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  BakedConversationHandle,
  ChatConversationHandle,
  isBaked,
} from '../handle';
import { BubbleMenu, useBubbleMenu } from './useBubbleMenu';
import {
  ConversationScroll,
  useConversationScroll,
} from './useConversationScroll';
import { ImagePreview, useImagePreview } from './useImagePreview';
import { MessageReaders, useMessageReaders } from './useMessageReaders';
import { useOpenChatCard } from './useOpenChatCard';

interface ConversationCoordinatorDeps {
  /** Provider-owned single source of truth for the message list. */
  messages$: BehaviorSubject<ChatMsg[]>;
  /** Current room handle — read when a send materialises the room/thread. */
  conversationHandle: ChatConversationHandle;
  /** Swap in the baked handle once a send materialises it (the coordinator
   * calls this off `messageSent$`, so the pipeline never touches the handle). */
  onConversationHandleMaterialized: (next: BakedConversationHandle) => void;
}

/**
 * The result of a send attempt, emitted on `messageSent$` for the store to
 * apply: `sent` reconciles the optimistic bubble to its server-canonical
 * message; `failed` flips it to the retry state — keeping the uploaded
 * payload when the upload succeeded but the persist didn't, so a retry
 * re-sends the real URL.
 */
export type MessageSendOutcome =
  | {
      status: 'sent';
      clientMessageId: string;
      messageId: string;
      roomId?: string;
      /** Set when the send materialised a thread; drives handle materialisation. */
      threadId?: string | null;
      payload: ChatUserMessagePayload;
    }
  | {
      status: 'failed';
      clientMessageId: string;
      finalPayload?: ChatUserMessagePayload;
    };

/** A composer-submitted edit: the new payload for an existing message. */
export interface EditSubmission {
  messageId: string;
  payload: ChatUserMessagePayload;
}

/** A reaction toggle on a message (from the menu pill or a reaction chip). */
export interface ReactIntent {
  message: ChatMsg;
  emoji: string;
}

export interface ConversationCoordinator {
  // ── view units
  scroll: ConversationScroll;
  imagePreview: ImagePreview;
  messageReaders: MessageReaders;
  /** The long-press action-menu overlay (presented from a bubble's rect). */
  bubbleMenu: BubbleMenu;
  /**
   * The active reply target — the ONLY public surface for it. The long-press
   * menu's 回复 sets it (`onReplyTo`); the composer + reply bar read it; the
   * send pipeline reads + clears it. Clear with `quoteTarget$.next(null)`.
   */
  quoteTarget$: BehaviorSubject<ChatMsg | null>;

  // ── event bus (domain hooks emit / subscribe)
  /** The store PUSHES on each real insert; the scroll follows-or-counts. */
  messageInserted$: Subject<ChatMsg>;
  /** The store PUSHES after a reconnect resync. */
  messagesResynced$: Subject<void>;
  /** A jump-to-quoted request; the store SUBSCRIBES to page the message in. */
  scrollToMessageRequested$: Subject<string>;
  /** The store PUSHES once paged in; the scroll SUBSCRIBES to jump + highlight. */
  scrollToLoadedMessage$: Subject<string>;
  /** A play-audio intent; the store SUBSCRIBES to mark + persist. */
  audioPlayRequested$: Subject<ChatMsg>;
  /** The menu's 编辑 — opens the composer's edit mode. The composer SUBSCRIBES. */
  editRequested$: Subject<ChatMsg>;
  /** The composer SUBMITS an edit; the store applies it optimistically + persists. */
  editSubmitted$: Subject<EditSubmission>;
  /** The menu's 撤回 — the store SUBSCRIBES to soft-delete + persist. */
  deleteRequested$: Subject<ChatMsg>;
  /** A reaction toggle (pill / chip) — the store SUBSCRIBES to persist it. */
  reactRequested$: Subject<ReactIntent>;
  /** A retry intent; the send pipeline SUBSCRIBES. */
  retryRequested$: Subject<ChatMsg>;
  /** The pipeline PUSHES the optimistic bubble; the store inserts it. */
  willSendMsg$: Subject<ChatMsg>;
  /** The pipeline PUSHES a send outcome; the store reconciles / marks failed,
   * and this hook materialises the room handle on success. */
  messageSent$: Subject<MessageSendOutcome>;
  /** The pipeline PUSHES before a retry; the store drops the failed bubble. */
  beforeResend$: Subject<string>;
  /** The composer PUSHES a (throttled) typing heartbeat; this hook broadcasts. */
  typing$: Subject<void>;
  /** The composer PUSHES on submit / clear; this hook broadcasts "stopped". */
  endTyping$: Subject<void>;
  /** A tapped bot `suggested_questions` chip; the composer SUBSCRIBES to send
   * the question back to the bot (auto @-mentioning it). */
  suggestQuestionRequested$: Subject<string>;

  // ── message-bubble interactions (express an intent or drive a view unit)
  /** Deep-link a tapped task/report card to its page. */
  onOpenCard: (payload: ChatMessagePayload) => void;
  /** Open the image viewer on ALL conversation images, at the tapped one. */
  onPreviewImage: (url: string) => void;
  /** Preview a video full-screen in-app (the `_mediaPreview` player). */
  onPreviewVideo: (url: string) => void;
  /** Open a non-previewable url externally (a file). */
  onOpenUrl: (url: string) => void;
  onQuotePress: (message: ChatMsg) => void;
  /** Long-press menu → set the reply target. */
  onReplyTo: (message: ChatMsg) => void;
  /** Long-press menu → open the composer's edit mode for this message. */
  onEditRequested: (message: ChatMsg) => void;
  /** Long-press menu → soft-delete this message. */
  onDeleteRequested: (message: ChatMsg) => void;
  /** Reaction pill / chip → toggle this emoji on the message. */
  onReact: (message: ChatMsg, emoji: string) => void;
  onShowReaders: (message: ChatMsg) => void;
  onAudioPlay: (message: ChatMsg) => void;
  onRetry: (message: ChatMsg) => void;
  /** A bot `suggested_questions` chip was tapped — ask it back to the bot. */
  onSuggestionPress: (question: string) => void;
}

export function useConversationCoordinator(
  deps: ConversationCoordinatorDeps,
): ConversationCoordinator {
  const { messages$, conversationHandle, onConversationHandleMaterialized } =
    deps;
  const { wsClient } = useChatSession();
  // Typing is broadcast only once the room exists (a bare chat has no room to
  // address — the first send materialises it).
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;

  // The reply target — set by long-press, read by the composer / reply bar /
  // send pipeline, cleared via `.next(null)`. The only public surface for it.
  const quoteTarget$ = useCreation(
    () => new BehaviorSubject<ChatMsg | null>(null),
    [],
  );

  // The bus.
  const messageInserted$ = useCreation(() => new Subject<ChatMsg>(), []);
  const messagesResynced$ = useCreation(() => new Subject<void>(), []);
  const scrollToMessageRequested$ = useCreation(
    () => new Subject<string>(),
    [],
  );
  const scrollToLoadedMessage$ = useCreation(() => new Subject<string>(), []);
  const audioPlayRequested$ = useCreation(() => new Subject<ChatMsg>(), []);
  const editRequested$ = useCreation(() => new Subject<ChatMsg>(), []);
  const editSubmitted$ = useCreation(() => new Subject<EditSubmission>(), []);
  const deleteRequested$ = useCreation(() => new Subject<ChatMsg>(), []);
  const reactRequested$ = useCreation(() => new Subject<ReactIntent>(), []);
  const retryRequested$ = useCreation(() => new Subject<ChatMsg>(), []);
  const willSendMsg$ = useCreation(() => new Subject<ChatMsg>(), []);
  const messageSent$ = useCreation(() => new Subject<MessageSendOutcome>(), []);
  const beforeResend$ = useCreation(() => new Subject<string>(), []);
  const typing$ = useCreation(() => new Subject<void>(), []);
  const endTyping$ = useCreation(() => new Subject<void>(), []);
  const suggestQuestionRequested$ = useCreation(
    () => new Subject<string>(),
    [],
  );

  // Typing transport: the composer expresses typing intents on the bus; the
  // coordinator turns them into WS frames — a heartbeat on `typing$`, a
  // "stopped" on `endTyping$` — but only once the room exists.
  useEffect(() => {
    if (!roomId) return;
    const subs = [
      typing$.subscribe(() => wsClient.sendTyping(roomId, false)),
      endTyping$.subscribe(() => wsClient.sendTyping(roomId, true)),
    ];
    return () => subs.forEach(s => s.unsubscribe());
  }, [roomId, wsClient, typing$, endTyping$]);

  // A first send to a bare peer (or into a deep-linked room with no thread)
  // materialises the room/thread; the outcome carries the now-concrete ids.
  // The coordinator owns the bus, so it reacts to the send outcome here and
  // swaps in the baked handle — the pipeline never touches the handle.
  useEffect(() => {
    const sub = messageSent$.subscribe(outcome => {
      if (outcome.status !== 'sent' || !outcome.roomId) return;
      const alreadyCurrent =
        isBaked(conversationHandle) &&
        conversationHandle.roomId === outcome.roomId &&
        conversationHandle.threadId === (outcome.threadId ?? null);
      if (alreadyCurrent) return;
      onConversationHandleMaterialized({
        kind: 'baked',
        roomId: outcome.roomId,
        threadId: outcome.threadId ?? null,
        roomType: isBaked(conversationHandle)
          ? conversationHandle.roomType
          : 'private',
        title: isBaked(conversationHandle)
          ? conversationHandle.title
          : conversationHandle.peer.name ?? '',
      });
    });
    return () => sub.unsubscribe();
  }, [messageSent$, conversationHandle, onConversationHandleMaterialized]);

  const scroll = useConversationScroll({
    messages$,
    messageInserted$,
    messagesResynced$,
    scrollToLoadedMessage$,
  });
  const imagePreview = useImagePreview();
  const messageReaders = useMessageReaders();
  const bubbleMenu = useBubbleMenu();
  const onOpenCard = useOpenChatCard();
  const rootNavigation = useNavigation<StackNavigationProp<AppNavParams>>();

  const { open: openImagePreview } = imagePreview;
  const { show: showReaders } = messageReaders;

  const onPreviewImage = useCallback(
    (url: string) => {
      // Open the viewer on EVERY conversation image so the user can swipe,
      // positioned at the tapped one (fall back to just it if not found).
      const images = collectImageUrls(messages$.getValue());
      const index = images.indexOf(url);
      openImagePreview(index >= 0 ? images : [url], index >= 0 ? index : 0);
    },
    [messages$, openImagePreview],
  );
  const onPreviewVideo = useCallback(
    (url: string) =>
      rootNavigation.navigate('_mediaPreview', { type: 'video', url }),
    [rootNavigation],
  );
  const onOpenUrl = useCallback((url: string) => {
    Linking.openURL(url).catch(onErrorIgnore);
  }, []);
  const onReplyTo = useCallback(
    (message: ChatMsg) => quoteTarget$.next(message),
    [quoteTarget$],
  );
  const onEditRequested = useCallback(
    (message: ChatMsg) => editRequested$.next(message),
    [editRequested$],
  );
  const onDeleteRequested = useCallback(
    (message: ChatMsg) => deleteRequested$.next(message),
    [deleteRequested$],
  );
  const onReact = useCallback(
    (message: ChatMsg, emoji: string) =>
      reactRequested$.next({ message, emoji }),
    [reactRequested$],
  );
  const onAudioPlay = useCallback(
    (message: ChatMsg) => audioPlayRequested$.next(message),
    [audioPlayRequested$],
  );
  const onRetry = useCallback(
    (message: ChatMsg) => retryRequested$.next(message),
    [retryRequested$],
  );
  const onQuotePress = useCallback(
    (message: ChatMsg) => {
      if (message.quoteMessageId) {
        scrollToMessageRequested$.next(message.quoteMessageId);
      }
    },
    [scrollToMessageRequested$],
  );
  const onSuggestionPress = useCallback(
    (question: string) => suggestQuestionRequested$.next(question),
    [suggestQuestionRequested$],
  );

  return useMemo(
    () => ({
      scroll,
      imagePreview,
      messageReaders,
      bubbleMenu,
      quoteTarget$,
      messageInserted$,
      messagesResynced$,
      scrollToMessageRequested$,
      scrollToLoadedMessage$,
      audioPlayRequested$,
      editRequested$,
      editSubmitted$,
      deleteRequested$,
      reactRequested$,
      retryRequested$,
      willSendMsg$,
      messageSent$,
      beforeResend$,
      typing$,
      endTyping$,
      suggestQuestionRequested$,
      onOpenCard,
      onPreviewImage,
      onPreviewVideo,
      onOpenUrl,
      onQuotePress,
      onReplyTo,
      onEditRequested,
      onDeleteRequested,
      onReact,
      onShowReaders: showReaders,
      onAudioPlay,
      onRetry,
      onSuggestionPress,
    }),
    [
      scroll,
      imagePreview,
      messageReaders,
      bubbleMenu,
      quoteTarget$,
      messageInserted$,
      messagesResynced$,
      scrollToMessageRequested$,
      scrollToLoadedMessage$,
      audioPlayRequested$,
      editRequested$,
      editSubmitted$,
      deleteRequested$,
      reactRequested$,
      retryRequested$,
      willSendMsg$,
      messageSent$,
      beforeResend$,
      typing$,
      endTyping$,
      suggestQuestionRequested$,
      onOpenCard,
      onPreviewImage,
      onPreviewVideo,
      onOpenUrl,
      onQuotePress,
      onReplyTo,
      onEditRequested,
      onDeleteRequested,
      onReact,
      showReaders,
      onAudioPlay,
      onRetry,
      onSuggestionPress,
    ],
  );
}

/**
 * Every previewable image url in the conversation, oldest → newest (the list
 * is newest-first), so the image viewer can swipe across all of them.
 */
function collectImageUrls(messages: ChatMsg[]): string[] {
  const urls: string[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const payload = messages[i].payload;
    if (payload.type === 'media') {
      if (payload.kind === 'image') urls.push(payload.url.split('#')[0]);
    } else if (payload.type === 'medias') {
      for (const item of payload.items) {
        if (item.kind === 'image') urls.push(item.url.split('#')[0]);
      }
    }
  }
  return urls;
}
